/**
 * Composed goal-recipe catalog entries for the beauty industry (plan §6.3 /
 * §6.5). Filled by the beauty P4b copywriting task — owned exclusively by it.
 * Entries omit `previewPath` (added automatically in template-catalog.ts).
 */
import type { TemplateEntryDraft } from '../template-catalog';

const BEAUTY_SUB_INDUSTRIES = [
  'ヘアサロン',
  'ネイルサロン',
  'アイラッシュ',
  'エステティック',
  'リラクゼーション',
  'プライベートサロン',
] as const;

export const COMPOSED_BEAUTY_TEMPLATES: readonly TemplateEntryDraft[] = [
  {
    slug: 'beauty-booking',
    theme: 'beauty',
    displayName: '予約で満席をつくるサロンサイト',
    category: '美容室・サロン',
    description:
      'ホーム上部と下部の2箇所に予約導線を置き、メニュー抜粋・スタイリスト紹介・アクセスをその間に並べる構成。メニュー一覧・予約・アクセスの専用ページも備え、「見て→安心して→予約する」までを迷わせずに進めます。',
    thumb: '/lp/template-beauty.webp',
    previewFixture: 'beauty-demo',
    fit: '新規予約を増やしたいサロン、Web予約を主軸にしたいサロン',
    features: [
      'ホーム上部・下部の二重予約導線',
      'メニュー抜粋＋メニュー一覧ページ',
      'スタイリスト紹介',
      '希望メニュー・日時を選べる予約フォーム',
      'アクセス専用ページ',
    ],
    goal: 'booking',
    industry: 'beauty',
    subIndustries: [...BEAUTY_SUB_INDUSTRIES],
    outcomeStory:
      '公開したその日から、サイトを見たお客様がそのまま予約フォームに進めるようになります。ホームの一番上に「ご予約はこちら」を置き、メニューやスタイリスト紹介を読み終えた下にももう一度予約への入り口を用意しているので、「あとで予約しよう」と離脱するお客様を減らせます。予約フォームには希望メニューと希望日時の項目があらかじめ入っているので、電話でのやり取りに使っていた時間を施術に充てられるようになります。',
    concerns: [
      {
        worry: '写真がまだ用意できていない',
        answer:
          '美容室向けのプロ撮影写真とそのまま使える初期文言があらかじめ入っています。写真は準備でき次第、差し替えていただけます。',
      },
      {
        worry: '予約フォームを自分で作れるか不安',
        answer:
          '希望メニュー・希望日時の項目まで用意した予約フォームが最初から使える状態になっています。スパム対策と通知メールも設定済みです。',
      },
      {
        worry: '開業後、更新が続けられるか不安',
        answer:
          'かんたん更新の機能で、メニューやお知らせを1分程度で追加できます。入力後はダッシュボードの「サイトを公開」ボタンを押すだけで反映されます。',
      },
    ],
    businessFlow: [
      '朝、前日に届いた予約フォームの通知メールを確認する',
      '新しいメニューやキャンペーンができたら、かんたん更新でメニューページに追加する',
      'ダッシュボードの「サイトを公開」ボタンを押して内容を反映する',
      '当面は電話予約と併用し、少しずつWeb予約の比率を増やしていく',
    ],
  },
  {
    slug: 'beauty-discovery',
    theme: 'beauty',
    displayName: '地図と検索から選ばれるサロンサイト',
    category: '美容室・サロン',
    description:
      'ホーム冒頭の「こだわり」直下にアクセス情報を配置し、初めて名前を知った方が「営業しているか・行けそうか」をすぐ確認できる構成。お知らせ欄も備え、検索から辿り着いた方に更新され続けているサロンだと伝えます。',
    thumb: '/lp/template-nail.webp',
    previewFixture: 'nail-demo',
    fit: '検索や地図から新しいお客様に見つけてほしいサロン、開業したばかりで認知を広げたいサロン',
    features: [
      'アクセス情報をファーストビュー直下に配置',
      'サロンのこだわり紹介',
      'メニュー抜粋',
      'お知らせ欄',
      'アクセス専用ページ',
    ],
    goal: 'discovery',
    industry: 'beauty',
    subIndustries: [...BEAUTY_SUB_INDUSTRIES],
    outcomeStory:
      '「駅からどれくらい」「今日はやっているか」——初めて店名を検索した方が知りたいのはまずそこです。このサイトはこだわり紹介のすぐ下にアクセス情報を置いているので、初訪問の方でも迷わず「行けそうだ」と判断できます。お知らせ欄も最初から用意されているので、営業日や季節のご案内を載せておけば、検索から来た方に「今も動いているサロン」だと伝わります。',
    concerns: [
      {
        worry: '文章を書くのが苦手',
        answer:
          'そのまま公開できる初期文言が入っています。まずは店名や住所など、お店固有の情報だけ直せば形になります。',
      },
      {
        worry: '地図アプリやSNSと連携できるか分からない',
        answer:
          '独自ドメインでの公開に対応しているので（プランによる）、Googleビジネスプロフィールなど外部の地図サービスにサイトURLとして登録していただけます。',
      },
      {
        worry: '更新を続けられるか不安',
        answer:
          'お知らせは1分で1件追加できるかんたん更新に対応しています。入力後はダッシュボードの「サイトを公開」ボタンで反映されます。',
      },
    ],
    businessFlow: [
      '開業前に住所・アクセス方法・営業時間を自分のお店の情報に書き換える',
      '開業後、季節の案内や臨時休業をお知らせ欄にかんたん更新で追加する',
      'ダッシュボードの「サイトを公開」ボタンで反映する',
      '地図サービスやSNSのプロフィールにサイトURLを載せて、検索からの入口を増やす',
    ],
  },
  {
    slug: 'beauty-trust',
    theme: 'beauty',
    displayName: 'スタイリストの人柄で選ばれるサロンサイト',
    category: '美容室・サロン',
    description:
      'ホームの上部でスタイリスト紹介を前面に出し、「誰が担当してくれるか」を先に見せてから、こだわりやご相談への導線に進む構成。スタイリスト紹介専用ページとご相談・ご予約ページを備え、指名や紹介での来店につなげます。',
    thumb: '/lp/template-private-salon.webp',
    previewFixture: 'private-salon-demo',
    fit: '担当を指名して通ってほしいサロン、口コミ・紹介での来店を増やしたいサロン',
    features: [
      'ホーム上部にスタイリスト紹介',
      'スタイリスト紹介専用ページ',
      'サロンのこだわり紹介',
      'ご相談・ご予約フォーム',
    ],
    goal: 'trust',
    industry: 'beauty',
    subIndustries: [...BEAUTY_SUB_INDUSTRIES],
    outcomeStory:
      '「どんな人が担当してくれるのか」が分かると、お客様は安心して問い合わせボタンを押せます。このサイトはホームの目立つ位置にスタイリスト紹介を置き、経歴や得意分野を伝えたうえで、ご相談・ご予約への案内につなげます。スタイリスト紹介は専用ページも用意されているので、指名のきっかけや「この人にお願いしたい」という紹介での来店を後押しします。',
    concerns: [
      {
        worry: 'スタッフの経歴や得意分野をどう見せればいいか分からない',
        answer:
          'スタイリスト紹介ページがあらかじめ用意されており、名前・肩書き・得意分野・経歴コメントを入力する形式になっています。そのまま公開できる初期文言も入っています。',
      },
      {
        worry: '実績や作例の写真がまだ用意できていない',
        answer:
          'この構成はスタイリストの人柄と経歴で信頼を伝える構成です。作例ギャラリーは含まれないため、写真がまだない状態でも公開いただけます。',
      },
      {
        worry: 'ご相談の問い合わせに対応しきれるか不安',
        answer:
          'ご相談・ご予約フォームには通知メールとスパム対策が付いています。配信に障害が起きた場合でも、フォームの受付自体は継続する仕組みです。',
      },
    ],
    businessFlow: [
      '開業前にスタイリスト紹介ページへ経歴・得意分野・写真を入力する',
      '新しいスタッフが加わったら、かんたん更新でスタイリスト紹介に追加する',
      'ダッシュボードの「サイトを公開」ボタンで反映する',
      'ご相談フォームに届いた内容を確認し、折り返しの連絡につなげる',
    ],
  },
  {
    slug: 'beauty-repeat',
    theme: 'beauty',
    displayName: '常連さんとつながり続けるサロンサイト',
    category: '美容室・サロン',
    description:
      'ホームの上部にお知らせ欄を置き、新メニューやキャンペーンをすぐ伝えられる構成。お知らせ専用ページとメニュー一覧ページも備え、「また来たくなる理由」を更新し続けられます。',
    thumb: '/lp/template-relaxation.webp',
    previewFixture: 'relaxation-demo',
    fit: '常連のお客様との関係を深めたいサロン、新メニューやキャンペーンをこまめに発信したいサロン',
    features: [
      'ホーム上部のお知らせ欄',
      'お知らせ専用ページ',
      'メニュー抜粋＋メニュー一覧ページ',
      'ご予約・お問合せ導線',
    ],
    goal: 'repeat',
    industry: 'beauty',
    subIndustries: [...BEAUTY_SUB_INDUSTRIES],
    outcomeStory:
      '常連のお客様は「次はいつ、何をお願いしよう」を探しに、時々サイトを覗きに来てくれます。このサイトはホームの目立つ位置にお知らせ欄を置いているので、新メニューやキャンペーン、季節のご案内を載せるたびに「またチェックしよう」と思ってもらえます。かんたん更新で1分あれば1件追加できるので、来店のたびにひとこと投稿する、といった続け方もできます。',
    concerns: [
      {
        worry: '更新を続けられるか一番不安',
        answer:
          'かんたん更新の簡易フォームなら1分で1件のお知らせを追加できます。入力後はダッシュボードの「サイトを公開」ボタンを押すだけで反映されます。',
      },
      {
        worry: '何を投稿すればいいか分からない',
        answer:
          '新メニューのお知らせ、営業日の変更、季節のご案内など、短い一言から始められる形式です。最初の投稿例もそのまま使える文言で入っています。',
      },
      {
        worry: '常連のお客様に気づいてもらえるか不安',
        answer:
          'お知らせはホームの上部に表示され、専用ページからも一覧で振り返れます。次回の来店時に話題にしていただきやすい導線です。',
      },
    ],
    businessFlow: [
      '来店対応の合間に、新メニューや季節のご案内をかんたん更新で入力する',
      'ダッシュボードの「サイトを公開」ボタンを押して反映する',
      '次回来店時のお客様との会話でお知らせの内容を話題にする',
      '前回の更新から日が空いていないか、ダッシュボードで確認する',
    ],
  },
];
